import React from 'react';
import PortalRing from './PortalRing';
import { agoLower } from '../../utils/surfaceFormat';

/**
 * The plate that comes over a Portal when it ends and that person has stories behind it.
 * Design handoff §4 "Interstitial".
 *
 * It slides OVER the Portal rather than replacing it, and the last frame holds and dims behind
 * it — so dismissing the plate puts you back on the Portal, not back on the rail.
 *
 * When the person has no other stories there is no plate at all: the viewer goes straight to
 * the next person. That rule lives in StoryViewer, not here.
 */
export default function StoryInterstitial({
  reel,
  nextHandle = null,
  onViewStories,
  onNext,
  onBackToFeatured,
  reduced = false,
}) {
  const name = (reel?.handle || reel?.displayName || 'them').toLowerCase();
  const count = reel?.stories?.length || 0;
  const oldest = reel?.stories?.[0]?.createdAt;

  return (
    <div className="absolute inset-0 z-30 flex flex-col justify-end">
      <div className="absolute inset-0" style={{ background: 'rgba(20,20,20,0.70)', transition: reduced ? 'none' : 'opacity 200ms linear' }} aria-hidden />

      <div
        className="pr-interstitial relative"
        style={{
          background: '#000000',
          borderTop: '2px solid #FFFFFF',
          padding: '22px 20px 20px',
          animation: reduced ? 'none' : 'prSlabUp 240ms cubic-bezier(0,0,0.2,1)',
        }}
      >
        <p className="font-mono font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.10em', color: 'rgba(255,255,255,0.60)' }}>
          Portal ended
        </p>

        <div className="mt-4 flex items-center" style={{ gap: 14 }}>
          <PortalRing state="unseen" size={64} avatarUrl={reel?.avatarUrl} initial={(reel?.displayName || '?')[0]} />
          <div>
            <p className="font-mono font-bold text-white" style={{ fontSize: 16 }}>{reel?.handle || reel?.displayName}</p>
            <p className="font-mono font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.10em', color: 'rgba(255,255,255,0.70)' }}>
              {count} {count === 1 ? 'STORY' : 'STORIES'}{oldest ? ` · FROM ${agoLower(oldest).toUpperCase()}` : ''}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={onViewStories}
          className="pr-story-action mt-5 w-full font-mono font-semibold uppercase"
          style={{ background: '#FFFFFF', color: '#000000', border: '2px solid #FFFFFF', fontSize: 14, letterSpacing: '0.05em', padding: '14px 16px', transition: 'background-color 200ms, color 200ms' }}
        >
          View all of {name}&apos;s stories
        </button>

        <button
          type="button"
          onClick={onNext}
          className="pr-story-secondary mt-2 flex w-full items-center justify-between font-mono font-bold uppercase text-white"
          style={{ border: '2px solid #FFFFFF', fontSize: 12, letterSpacing: '0.10em', padding: '12px 16px', transition: 'background-color 200ms, color 200ms' }}
        >
          <span>Next</span>
          {nextHandle && <span style={{ opacity: 0.7 }}>{nextHandle.toUpperCase()} →</span>}
        </button>

        <button
          type="button"
          onClick={onBackToFeatured}
          className="mt-3 w-full text-center font-mono font-bold uppercase text-white/60 hover:text-white"
          style={{ fontSize: 11, letterSpacing: '0.10em', padding: '8px 0', transition: 'color 200ms' }}
        >
          Back to featured
        </button>
      </div>
    </div>
  );
}
